import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApprovedHousingComplex, AcademicSession, CampusWardSessionInformation, AddressWardAssignment, ApprovedAddress } from '../../models';
import { ApprovedHousingSessionInformation } from '../../index';
import { Observable, of } from 'rxjs';
import { retry, tap } from 'rxjs/operators';

@Injectable()
export class HousingDataService {
  private baseUrl = 'api/approved-housing';
  private wardAddressesUrl = 'api/campus-units/wards';

  // We want to cache the housing complexes because they're unlikely to change while the user is in the system
  private cachedHousingComplexes: ApprovedHousingComplex[] = null;

  // We want to cache the sessions because they're unlikely to change while the user is in the system
  private cachedSessions: AcademicSession[];

  constructor(private http: HttpClient) {}

  /**
   * Gets the current approved housing complexes.  Uses our cache if already cached, otherwise goes to the server
   * (where it will retry up to two times).
   */
  getCurrentApprovedHousingComplexes(): Observable<ApprovedHousingComplex[]> {
    if (this.cachedHousingComplexes != null) {
      return of(this.cachedHousingComplexes);
    }

    return this.http
      .get<ApprovedHousingComplex[]>(`${this.baseUrl}/complexes`)
      .pipe(
        retry(2),
        tap(complexes => (this.cachedHousingComplexes = complexes))
      );
  }

  /**
   * Gets the academic sessions that you can use to search for wards based on approved housing.
   * Uses our cache if already cached, otherwise goes to the server (where it will retry up to two times).
   */
  getAcademicSessions(): Observable<AcademicSession[]> {
    if (this.cachedSessions != null) {
      return of(this.cachedSessions);
    }

    return this.http
      .get<AcademicSession[]>(`${this.baseUrl}/sessions`)
      .pipe(
        retry(2),
        tap(sessions => (this.cachedSessions = sessions))
      );
  }

  /**
   * Gets the wards and addresses assigned to those wards for the complex and session.
   */
  getCampusWardSessionInformation(complexId: number, sessionNumber: number): Observable<CampusWardSessionInformation[]> {
    const params = new HttpParams().set('sessionNumber', `${sessionNumber}`);

    return this.http.get<CampusWardSessionInformation[]>(`${this.baseUrl}/complexes/${complexId}/wards`, {
      params: params
    });
  }

  /**
   * Gets the housing complexes and addresses assigned to the campus ward for the given session.
   */
  getHousingComplexSessionInformation(campusWardId: number, sessionNumber: number, wardGroupTypeId?: number): Observable<ApprovedHousingSessionInformation[]> {
    let url = `${this.wardAddressesUrl}/${campusWardId}/sessions/${sessionNumber}/addresses`;
    if (wardGroupTypeId) {
      url = `${url}?campusWardGroupTypeId=${wardGroupTypeId}`;
    }

    return this.http.get<ApprovedHousingSessionInformation[]>(url);
  }

  /**
   * Updates the ward assigned to the addresses for the semester.
   */
  updateWardAssignment(campusWardId: number, sessionNumber: number, addresses: string[]) {
    return this.http.put(`${this.baseUrl}/address-ward-assignments`, new AddressWardAssignment(addresses, sessionNumber, campusWardId));
  }

  /**
   * Clears the ward assigned to the addresses for the semester.
   */
  removeWardAssignment(sessionNumber: number, addresses: string[]) {
    // same API to clear, just don't pass a ward
    return this.http.put(`${this.baseUrl}/address-ward-assignments`, new AddressWardAssignment(addresses, sessionNumber));
  }

  /**
   * Gets the assigned approved addresses for the ward
   * @param campusWardId WardId
   * @param session Semester
   */
  getAssignedApprovedWardAddresses(campusWardId: number, session: number): Observable<ApprovedAddress[]> {
    const url = `${this.baseUrl}/${campusWardId}/term/${session}/AssignedAddresses`;
    return this.http.get<ApprovedAddress[]>(url);
  }
}
